import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinalDecisionSummary = ({ selectedRecommendation, onExportReport, onProceedToImplementation }) => {
  if (!selectedRecommendation) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
          <Icon name="AlertCircle" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Belum Ada Rekomendasi Terpilih</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Pilih salah satu rekomendasi strategis di atas untuk melihat ringkasan keputusan final dan langkah implementasi.
        </p>
      </div>
    );
  }

  const getVariantColor = () => {
    switch (selectedRecommendation?.variant) {
      case 'aggressive':
        return 'var(--color-success)';
      case 'controlled':
        return 'var(--color-primary)';
      case 'hold':
        return 'var(--color-warning)';
      case 'restructure':
        return 'var(--color-destructive)';
      default:
        return 'var(--color-foreground)';
    }
  };

  return (
    <div className="card">
      <div className="text-center mb-8">
        <div 
          className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center"
          style={{ backgroundColor: `${getVariantColor()}15` }}
        >
          <Icon name={selectedRecommendation?.icon} size={40} color={getVariantColor()} />
        </div>
        <h2 className="text-2xl font-semibold mb-2">Keputusan Final: {selectedRecommendation?.title}</h2>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          {selectedRecommendation?.description}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="p-4 rounded-lg bg-muted/50 text-center">
          <Icon name="DollarSign" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
          <p className="text-xs text-muted-foreground mb-1">Total Investasi</p>
          <p className="text-lg font-bold data-text">{selectedRecommendation?.investmentRequired}</p>
        </div>

        <div className="p-4 rounded-lg bg-muted/50 text-center">
          <Icon name="Clock" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
          <p className="text-xs text-muted-foreground mb-1">Timeline</p>
          <p className="text-lg font-bold">{selectedRecommendation?.timeline}</p>
        </div>

        <div className="p-4 rounded-lg bg-muted/50 text-center">
          <Icon name="TrendingUp" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
          <p className="text-xs text-muted-foreground mb-1">Expected ROI</p>
          <p className="text-lg font-bold">{selectedRecommendation?.expectedROI}</p>
        </div>

        <div className="p-4 rounded-lg bg-muted/50 text-center">
          <Icon name="Target" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
          <p className="text-xs text-muted-foreground mb-1">Confidence</p>
          <p className="text-lg font-bold data-text">{selectedRecommendation?.confidenceScore}%</p>
        </div>
      </div>
      <div className="border-t border-border pt-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Langkah Implementasi Prioritas</h3>
        <div className="space-y-3">
          {selectedRecommendation?.nextSteps?.map((step, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold mb-1">{step?.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{step?.description}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    <span>{step?.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Users" size={14} />
                    <span>{step?.responsible}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-border pt-6 mb-6">
        <div className="p-4 rounded-lg bg-warning/5 border border-warning/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={24} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-warning mb-2">Risiko Kritis yang Perlu Diperhatikan</h4>
              <ul className="space-y-2">
                {selectedRecommendation?.criticalRisks?.map((risk, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-warning mt-1">•</span>
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="default"
          fullWidth
          onClick={onProceedToImplementation}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Lanjut ke Implementasi
        </Button>
        
        <Button
          variant="outline"
          fullWidth
          onClick={onExportReport}
          iconName="Download"
          iconPosition="left"
        >
          Export Laporan PDF
        </Button>
      </div>
      <div className="mt-6 text-center">
        <p className="text-xs text-muted-foreground">
          Keputusan ini didasarkan pada analisis komprehensif data operasional, keuangan, dan pasar dengan dukungan AI confidence score {selectedRecommendation?.confidenceScore}%
        </p>
      </div>
    </div>
  );
};

export default FinalDecisionSummary;